import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import axios from 'axios';
import ImageFloorPlanHeatmap, { FLOOR_PLAN_MACHINES, FLOOR_PLAN_ZONE_BOUNDS } from './ImageFloorPlanHeatmap';

// API Base URL from environment
const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/simulation/live/data/`;

/**
 * Individual TL Live Feed Component (IIT Bombay Edition)
 * Uses SVG-based floor plan for precise positioning
 */
const TLIndividualLiveFeed = ({ siteId = 2 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [machineVisits, setMachineVisits] = useState({}); // Track machine visit counts for heatmap

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${API_BASE}?site_id=${siteId}`);
                setData(response.data);
                setLoading(false);

                // Simulate machine visits based on active machines (in production, this would come from API)
                // Track cumulative visits for heatmap intensity
                if (response.data?.live_machines) {
                    setMachineVisits(prev => {
                        const updated = { ...prev };
                        response.data.live_machines.forEach(machine => {
                            if (machine.status === 'active') {
                                updated[machine.id] = (updated[machine.id] || 0) + 1;
                            }
                        });
                        return updated;
                    });
                }
            } catch (err) {
                console.error("Error fetching live simulation data:", err);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 2000);
        return () => clearInterval(interval);
    }, [siteId]);

    // Count users in a zone
    const getUsersInZone = (zoneKey) => {
        const zone = FLOOR_PLAN_ZONE_BOUNDS[zoneKey];
        if (!zone || !data?.live_users) return 0;

        return data.live_users.filter(user => {
            const x = parseFloat(user.x);
            const y = parseFloat(user.y);
            return x >= zone.minX && x <= zone.maxX && y >= zone.minY && y <= zone.maxY;
        }).length;
    };

    // Get total machine visits for a zone (for heatmap panel)
    const getZoneMachineVisits = (zoneKey) => {
        let total = 0;
        Object.entries(FLOOR_PLAN_MACHINES).forEach(([key, machine]) => {
            if (machine.zone === zoneKey) {
                total += machineVisits[machine.id] || 0;
            }
        });
        return total;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                    🔴 {data?.site_name} Live Feed
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ bgcolor: '#e3f2fd', px: 2, py: 1, borderRadius: '8px', border: '1px solid #bbdefb' }}>
                        <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>
                            {data?.occupancy || 0} / {data?.max_capacity || 20} Occupancy
                        </Typography>
                    </Box>

                    <Box sx={{ bgcolor: '#f1f8e9', px: 2, py: 1, borderRadius: '8px', border: '1px solid #dcedc8' }}>
                        <Typography sx={{ color: '#388e3c', fontWeight: 700 }}>
                            {data?.live_machines?.filter(m => m.status === 'active').length || 0} Machines Active
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Main Floor Plan */}
                <Grid item xs={12} lg={9}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '24px',
                            border: '1px solid #e6e6e6',
                            bgcolor: '#fff',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Heatmap Floor Plan */}
                        <ImageFloorPlanHeatmap
                            liveUsers={data?.live_users || []}
                            liveMachines={data?.live_machines || []}
                            machineVisits={machineVisits}
                            showHeatmap={true}
                            showMachines={true}
                            showUsers={true}
                            showZoneLabels={false}
                            onMachineClick={(key, machine) => {
                                console.log('Machine clicked:', key, machine);
                            }}
                            onZoneClick={(key, zone) => {
                                console.log('Zone clicked:', key, zone);
                            }}
                        />
                    </Paper>
                </Grid>

                {/* Zone Activity Panel */}
                <Grid item xs={12} lg={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: '24px',
                            border: '1px solid #e6e6e6',
                            height: '100%'
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, mb: 2 }}>Zone Activity</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {[
                                { key: 'MACHINE_SHOP', label: 'MACHINE SHOP', color: '#e8f4f8', icon: '🔧' },
                                { key: 'ELECTRONICS', label: 'ELECTRONICS', color: '#fff8e1', icon: '⚡' },
                                { key: 'WORKBENCH', label: 'WORKBENCH', color: '#fff3e0', icon: '🛠️' },
                                { key: 'PRINTING_3D', label: '3D PRINTING', color: '#e8f5e9', icon: '🖨️' },
                                { key: 'CONFERENCE', label: 'CONFERENCE', color: '#fce4ec', icon: '👥' },
                                { key: 'ADMIN', label: 'ADMIN/RECEPTION', color: '#fff8e1', icon: '📋' },
                                { key: 'SMALL_OFFICE', label: 'SMALL OFFICE', color: '#e3f2fd', icon: '🏢' },
                            ].map(zone => {
                                const count = getUsersInZone(zone.key);
                                const visits = getZoneMachineVisits(zone.key);
                                return (
                                    <Box key={zone.key} sx={{ p: 1.5, borderRadius: '12px', bgcolor: zone.color }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>
                                                {zone.icon} {zone.label}
                                            </Typography>
                                            {visits > 0 && (
                                                <Typography sx={{ fontSize: '10px', color: '#f57c00', fontWeight: 600 }}>
                                                    🔥 {visits}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                            {count} People
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Machine List */}
                        <Typography sx={{ fontWeight: 600, mt: 3, mb: 2 }}>Active Machines</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '200px', overflowY: 'auto' }}>
                            {(data?.live_machines || []).filter(m => m.status === 'active').map(machine => (
                                <Box
                                    key={machine.id}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '8px',
                                        bgcolor: '#f0fff4',
                                        border: '1px solid #c8e6c9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                                            {machine.name || `Machine #${machine.id}`}
                                        </Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#666' }}>
                                            {machine.load}% Load
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                            {(!data?.live_machines || data.live_machines.filter(m => m.status === 'active').length === 0) && (
                                <Typography sx={{ fontSize: '12px', color: '#999', textAlign: 'center', py: 2 }}>
                                    No active machines
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TLIndividualLiveFeed;
